import { useLocation, useNavigate } from 'react-router-dom';
import { useGetMultipleRecordsQuery, useMergeContactsMutation } from '../../api/contactApiSlice';
import MergeDuplicateSkeleton from '../merge/MergeDuplicateSkeleton';
import { useTheme } from '../../middleware/ThemeContext';
import { useToast } from '../../components/toast/ToastContext';
import { useEffect, useRef, useState } from 'react';
import * as Yup from "yup";
import { Form, Formik } from 'formik';
import StyledText from '../../components/StyledComponents/StyledText';
import MergeConfirmPopup from '../merge/MergeConfirmPopup';
import MergePreview from '../merge/MergePreview';
import PrivateNotesMerge from '../merge/PrivateNotesMerge';
import MergeSingleResult from '../merge/MergeSingleResult';
import { Container, Title, TitleCount, TitleGroup, TitleWrapper } from '../../components/listing/AMSListing';
import TwoUsersIcon from '../../assets/icons/TwoUsersIcon';
import CloseIcon from '../../assets/icons/CloseIcon';
import { Button } from '../../components/form/Button';
import styled from 'styled-components';
import NoData from '../../components/listing/NoData';
import { Data } from '../../types/types';
import { extractUniqueFields, isFieldRequired, isFieldRequiredFromRecords } from '../../utils/helper';
import ArrowIcon from '../../assets/icons/ArrowIcon';

const allFields = [
    {
        label: 'First Name',
        key: 'firstName'
    },
    {
        label: 'Last Name',
        key: 'lastName'
    },
    {
        label: 'Full Name',
        key: 'fullName'
    },
    {
        label: 'Email',
        key: 'email'
    },
    {
        label: 'Organizations',
        key: 'contactOrganizations'
        // key: 'contactOrgRoles'
    },
    // {
    //     label: 'Contact Roles',
    //     key: 'contactRoles'
    // },
];

function buildValidationSchema(
  dynamicFields: { 
  crmFieldValues?: { id: number; name: string }[], 
  submissionFields?: { id: number; name: string }[] 
},
  records: any[]
) {
  const schemaShape: Record<string, any> = {
    dataID: Yup.number().required("Primary ID is required"),
    firstName: isFieldRequired("firstName",records)
      ? Yup.string().required("First Name is required")
      : Yup.string(),
    lastName: isFieldRequired("lastName",records)
      ? Yup.string().required("Last Name is required")
      : Yup.string(),
    fullName: isFieldRequired("fullName",records)
      ? Yup.string().required("Full Name is required")
      : Yup.string(),
    email: isFieldRequired("email",records)
      ? Yup.string().email("Invalid email").required("Email is required")
      : Yup.string().email("Invalid email"),
    contactOrgRoles: isFieldRequired("contactOrgRoles", records)
      ? Yup.string().required("Organizations are required")
      : Yup.string(),
    // contactRoles: isFieldRequired("contactRoles", records)
    //   ? Yup.string().required("Contact Roles is required")
    //   : Yup.string(),
  };

  const allDynamicFields = [
    ...(dynamicFields.crmFieldValues ?? []),
    ...(dynamicFields.submissionFields ?? []),
  ];

  allDynamicFields.forEach((field) => {
    const fieldKey = `${field.name}_${field.id}`;

    schemaShape[fieldKey] = isFieldRequiredFromRecords(records, field.id)
      ? Yup.string().required(`${field.name} is required`)
      : Yup.string();
  });

  return Yup.object().shape(schemaShape);
}

const MergeContacts = () => {
    const noDataRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const idsParam = query.get('ids');

    const {theme} = useTheme();
        const showToast = useToast();
        const navigate = useNavigate();
        const [showPopup, setShowPopup] = useState(false);
    const initial_state: Data = {
      metaData: {
          id: 0,
          image: "",
          firstName: {
              data_id: 0,
              value: "",
          },
          lastName: {
              data_id: 0,
              value: "",
          },
          fullName: {
                data_id: 0,
                value: "",
          },
          email: {
              data_id: 0,
              value: "",
          },
          contactOrganizations: {
            data_id: 0,
            orgIds: [],
            orgNames: "",
            // orgRoleIds: []
          },
        //   organization: {
        //       data_id: 0,
        //       id: 0,
        //       name: "",
        //       image: "",
        //   },
        //   contactRoles: {
        //       data_id: 0,
        //       roleids: [],
        //       roleNames: "",
        //   },
      },
      formSubmissions: [],
      Ids: [],
      privateNotes: [],
  };
  const [selectedData, setSelectedData] = useState<Data>(initial_state);

  const [mergeContacts, { isLoading: loadingMerge, isSuccess, isError, error: err }] = useMergeContactsMutation();

  const idsArray = idsParam
    ? idsParam.split(',').map(id => Number(id.trim()))
    : [];

  const { data: recordData, isLoading, error } = useGetMultipleRecordsQuery(idsParam, {
    skip: !idsParam, refetchOnMountOrArgChange: true
    });

  const dynamicFields = extractUniqueFields(recordData);

  // track whether we've already auto-applied defaults on load
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const formikRef = useRef<any>(null);

  // helper: compute a common field value across all records
  const computeCommonValue = (records: any[], extractor: (rec: any) => string): string | null => {
      const values = records.map(extractor).filter(v => !shouldSkipValue(v));
      if(values.length === 0) return null;
      const unique = Array.from(new Set(values));
      return unique.length === 1 ? unique[0] : null;
  };

  function transformOrgRoles(
    orgIds: number[],
    selectedId: number,
    allData: any[]
    ): { organizationId: number; rolesId: number[]; isPrimary: boolean }[] {
    
    const allContactOrgs = allData.flatMap(
        (contact) => contact.contactOrganizations || []
    );

    return orgIds.map((orgId) => {
        
        const orgEntries = allContactOrgs.filter(
        (entry) => entry.organization?.id === orgId
        );

        const roleIds = orgEntries.flatMap((entry) =>
        (entry.contactOrgRoles || []).map((cor: any) => cor.role?.id)
        ).filter((id): id is number => typeof id === 'number');

        const uniqueRoleIds = Array.from(new Set(roleIds));

        const isPrimary = orgId === selectedId || orgEntries.some(entry => entry.isPrimary === true);

        return {
        organizationId: orgId,
        rolesId: uniqueRoleIds,
        isPrimary,
        };
    });
    }

  const handleMetaIdUpdate = (newId: number, image: string) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            return {
            ...prev,
            metaData: {
                ...prev.metaData,
                id: newId,
                image: image,
            },
            };
        });
    };
    const updateMetaDataField = (key: string, value: any) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            return {
            ...prev,
            metaData: {
                ...prev.metaData,
                [key]: value,
            },
            };
        });
    };
    const updateIds = (newIds: number[]) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            return {
                ...prev,
                Ids: newIds,
                };
            }
        );
    };
    const updateFields = (data_id: number, id: number, name: string, value: string) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            const submissions = prev.formSubmissions || [];

            const index = submissions.findIndex(item => item.id === id);

            let updatedSubmissions;

            if (index !== -1) {
                updatedSubmissions = [...submissions];
                updatedSubmissions[index] = { data_id, id, name, value };
            } else {
                updatedSubmissions = [...submissions, { data_id, id, name, value }];
            }

            return {
                ...prev,
                formSubmissions: updatedSubmissions
            };
        });
    };
    
    const updatePrivateNotes = (data_id: number, pNotesCount: number) => {
        setSelectedData(prev => {
            if (!prev) return prev;

            const submissions = prev.privateNotes || [];

            const index = submissions.findIndex(item => item.data_id === data_id);

            let updatedNotes;

            if (index !== -1) {
                updatedNotes = submissions.filter(item => item.data_id !== data_id);
            } else {
                updatedNotes = [...submissions, { data_id, pNotesCount }];
            }

            return {
                ...prev,
                privateNotes: updatedNotes
            };
        });
    };
    
    useEffect(() => {
        const duplicateIDS = idsArray?.filter(id => id !== selectedData.metaData.id) || [];
        updateIds(duplicateIDS);

    },[selectedData.metaData.id]);
    
    const crmFieldsValues = dynamicFields.crmFieldValues.reduce((acc, field) => {
        acc[`${field.name+'_'+field.id}`] = '';
        return acc;
    }, {} as Record<string, any>);

    const submissionFields = dynamicFields.submissionFields.reduce((acc, field) => {
        acc[`${field.name+'_'+field.id}`] = '';
        return acc;
    }, {} as Record<string, any>);
    
    const initialValues = {
        dataID: null,
        firstName: '',
        lastName: '',
        fullName: '',
        email: '',
        contactOrganizations: '',
        // contactRoles: '',
        ...crmFieldsValues,
        ...submissionFields,
    }

    const validationSchema = buildValidationSchema(dynamicFields, recordData);

    // automatic default value application effect
    useEffect(() => {
        if (!defaultsApplied && recordData && recordData.length > 0 && formikRef.current) {
            const primary = recordData[0];

            // Helper: Get all non-empty values for a field with their source contact ID
            const getAllValidValues = (extractor: (c: any) => any): { value: any; data_id: number }[] => {
                return recordData
                    .map((c: any) => {
                        const val = extractor(c);
                        return val && !shouldSkipValue(val) ? { value: val, data_id: c.id } : null;
                    })
                    .filter((item: { value: any; data_id: number } | null): item is { value: any; data_id: number } => item !== null);
            };

            // Helper: Return value ONLY if ALL non-empty values are identical (consensus)
            const getConsensusValue = (extractor: (c: any) => any): { value: any; data_id: number } | null => {
                const validValues = getAllValidValues(extractor);
                if (validValues.length === 0) return null;
                
                const uniqueValues = Array.from(new Set(validValues.map((v: { value: any; data_id: number }) => v.value)));
                // ✅ Return only if there's exactly ONE unique value across ALL contacts
                if (uniqueValues.length === 1) {
                    return validValues[0];
                }
                // ❌ Multiple different values → no consensus → return null
                return null;
            };

            handleMetaIdUpdate(primary.id, primary.image);
            formikRef.current.setFieldValue('dataID', primary.id);

            // Standard meta fields: firstName, lastName, fullName, email
            ['firstName', 'lastName', 'fullName', 'email'].forEach((key: string) => {
                const extractor = (r: any) => getFieldValue(r, key);
                
                // 1️⃣ Check for consensus across ALL records
                const consensus = getConsensusValue(extractor);
                if (consensus && !shouldSkipValue(consensus.value)) {
                    updateMetaDataField(key, { data_id: consensus.data_id, value: consensus.value });
                    formikRef.current.setFieldValue(key, consensus.value);
                    return; // ✅ Consensus found → use it
                }

                // 2️⃣ No consensus → Check if primary is empty
                const primaryValue = getFieldValue(primary, key);
                if (shouldSkipValue(primaryValue)) {
                    // Primary empty → look for consensus among OTHER contacts only
                    const otherContacts = recordData.filter((c: any) => c.id !== primary.id);
                    const otherValidValues = otherContacts
                        .map((c: any) => {
                            const val = extractor(c);
                            return val && !shouldSkipValue(val) ? { value: val, data_id: c.id } : null;
                        })
                        .filter((item: { value: any; data_id: number } | null): item is { value: any; data_id: number } => item !== null);
                    
                    if (otherValidValues.length > 0) {
                        const uniqueOtherValues = Array.from(new Set(otherValidValues.map((v: { value: any; data_id: number }) => v.value)));
                        // ✅ Only use fallback if OTHER contacts agree on ONE value
                        if (uniqueOtherValues.length === 1) {
                            updateMetaDataField(key, { data_id: otherValidValues[0].data_id, value: otherValidValues[0].value });
                            formikRef.current.setFieldValue(key, otherValidValues[0].value);
                            return;
                        }
                    }
                    // ❌ Other contacts disagree or all empty → leave empty
                }
                // 3️⃣ Primary has value BUT no consensus → ❌ SKIP (conflict detected)
                // Do nothing → leave field empty for user to decide manually
            });

            // Organizations field with same logic
            const orgExtractor = (r: any) => getOrganizationNames(r.contactOrganizations);
            const orgConsensus = getConsensusValue(orgExtractor);
            
            if (orgConsensus && !shouldSkipValue(orgConsensus.value)) {
                const sourceContact = recordData.find((c: any) => c.id === orgConsensus.data_id);
                const orgIds = getOrganizationsIDS(sourceContact?.contactOrganizations || []);
                updateMetaDataField('contactOrganizations', {
                    data_id: orgConsensus.data_id,
                    orgIds,
                    orgNames: orgConsensus.value,
                });
                formikRef.current.setFieldValue('contactOrganizations', orgConsensus.value);
            } else {
                const primaryOrgNames = getOrganizationNames(primary.contactOrganizations);
                if (shouldSkipValue(primaryOrgNames) || primaryOrgNames === '-') {
                    // Primary empty → check consensus among others
                    const otherContacts = recordData.filter((c: any) => c.id !== primary.id);
                    const otherOrgValues = otherContacts
                        .map((c: any) => {
                            const val = getOrganizationNames(c.contactOrganizations);
                            return val && !shouldSkipValue(val) && val !== '-' 
                                ? { value: val, data_id: c.id } 
                                : null;
                        })
                        .filter((item: { value: any; data_id: number } | null): item is { value: any; data_id: number } => item !== null);
                    
                    if (otherOrgValues.length > 0) {
                        const uniqueOtherOrgs = Array.from(new Set(otherOrgValues.map((v: { value: any; data_id: number }) => v.value)));
                        if (uniqueOtherOrgs.length === 1) {
                            const sourceContact = otherContacts.find((c: any) => c.id === otherOrgValues[0].data_id);
                            const orgIds = getOrganizationsIDS(sourceContact?.contactOrganizations || []);
                            updateMetaDataField('contactOrganizations', {
                                data_id: otherOrgValues[0].data_id,
                                orgIds,
                                orgNames: otherOrgValues[0].value,
                            });
                            formikRef.current.setFieldValue('contactOrganizations', otherOrgValues[0].value);
                        }
                    }
                    // ❌ No consensus among others → leave empty
                }
                // ❌ Primary has orgs but no consensus → SKIP (conflict)
            }

            // Dynamic submission fields
            dynamicFields?.submissionFields?.forEach((field: { name: string; id: number }) => {
                const extractor = (r: any) => {
                    const m = r?.cfr?.formSubmissions?.submissionValues?.find((s: any) => s.field?.id === field.id);
                    return m?.value ?? '-';
                };
                const fieldKey = `${field.name}_${field.id}`;

                // 1️⃣ Consensus across ALL
                const consensus = getConsensusValue(extractor);
                if (consensus && !shouldSkipValue(consensus.value)) {
                    updateFields(consensus.data_id, field.id, field.name, consensus.value);
                    formikRef.current.setFieldValue(fieldKey, consensus.value);
                    return;
                }

                // 2️⃣ Primary empty → check others for consensus
                const primaryMatched = primary?.cfr?.formSubmissions?.submissionValues?.find(
                    (s: any) => s.field?.id === field.id
                );
                const primaryValue = primaryMatched?.value ?? '-';
                
                if (shouldSkipValue(primaryValue)) {
                    const otherContacts = recordData.filter((c: any) => c.id !== primary.id);
                    const otherValidValues = otherContacts
                        .map((c: any) => {
                            const m = c?.cfr?.formSubmissions?.submissionValues?.find((s: any) => s.field?.id === field.id);
                            const val = m?.value ?? '-';
                            return val && !shouldSkipValue(val) ? { value: val, data_id: c.id } : null;
                        })
                        .filter((item: { value: any; data_id: number } | null): item is { value: any; data_id: number } => item !== null);
                    
                    if (otherValidValues.length > 0) {
                        const uniqueValues = Array.from(new Set(otherValidValues.map((v: { value: any; data_id: number }) => v.value)));
                        if (uniqueValues.length === 1) {
                            updateFields(otherValidValues[0].data_id, field.id, field.name, otherValidValues[0].value);
                            formikRef.current.setFieldValue(fieldKey, otherValidValues[0].value);
                            return;
                        }
                    }
                }
                // ❌ No consensus OR primary conflicts → skip
            });

            // Dynamic CRM fields (same pattern)
            dynamicFields?.crmFieldValues?.forEach((field: { name: string; id: number }) => {
                const extractor = (r: any) => {
                    const m = r?.cfr?.crmFieldValues?.find((s: any) => s.field?.id === field.id);
                    return m?.value ?? '-';
                };
                const fieldKey = `${field.name}_${field.id}`;

                // 1️⃣ Consensus across ALL
                const consensus = getConsensusValue(extractor);
                if (consensus && !shouldSkipValue(consensus.value)) {
                    updateFields(consensus.data_id, field.id, field.name, consensus.value);
                    formikRef.current.setFieldValue(fieldKey, consensus.value);
                    return;
                }

                // 2️⃣ Primary empty → check others for consensus
                const primaryMatched = primary?.cfr?.crmFieldValues?.find(
                    (s: any) => s.field?.id === field.id
                );
                const primaryValue = primaryMatched?.value ?? '-';
                
                if (shouldSkipValue(primaryValue)) {
                    const otherContacts = recordData.filter((c: any) => c.id !== primary.id);
                    const otherValidValues = otherContacts
                        .map((c: any) => {
                            const m = c?.cfr?.crmFieldValues?.find((s: any) => s.field?.id === field.id);
                            const val = m?.value ?? '-';
                            return val && !shouldSkipValue(val) ? { value: val, data_id: c.id } : null;
                        })
                        .filter((item: { value: any; data_id: number } | null): item is { value: any; data_id: number } => item !== null);
                    
                    if (otherValidValues.length > 0) {
                        const uniqueValues = Array.from(new Set(otherValidValues.map((v: { value: any; data_id: number }) => v.value)));
                        if (uniqueValues.length === 1) {
                            updateFields(otherValidValues[0].data_id, field.id, field.name, otherValidValues[0].value);
                            formikRef.current.setFieldValue(fieldKey, otherValidValues[0].value);
                            return;
                        }
                    }
                }
                // ❌ No consensus OR primary conflicts → skip
            });

            setDefaultsApplied(true);
            setTimeout(() => formikRef.current?.validateForm(), 0);
        }
    }, [recordData, defaultsApplied, dynamicFields]);
    
    const ConfirmMerge = async () => {
        const allFields = selectedData.formSubmissions.map(({id, value}) => ({
            fieldId: id,
            value,
        }));

        const privateNotesIDS = selectedData.privateNotes?.map(note => note.data_id);
        const OrganizationRoles = transformOrgRoles(selectedData?.metaData?.contactOrganizations?.orgIds || [1], selectedData.metaData.contactOrganizations?.data_id || 1, recordData);

        const mergePayload = {
            "id": selectedData.metaData.id,
            "duplicateContactId": selectedData.Ids,
            "privateNotes": privateNotesIDS,
            "contactMeta": {
                "firstName": selectedData?.metaData?.firstName?.value,
                "lastName": selectedData?.metaData?.lastName?.value,
                "fullName": selectedData?.metaData?.fullName?.value,
                "email": selectedData?.metaData?.email?.value,
                "image": selectedData.metaData.image,
                // "roles": selectedData?.metaData?.contactRoles?.roleids,
                // "organizationRoles": selectedData?.metaData?.contactOrgRoles?.orgIds,
                "organizationRoles": OrganizationRoles,
            },
            "fields": allFields,
        }
        
        try {
        const result = await mergeContacts(mergePayload).unwrap(); 
        showToast('Success', 'Merged contacts successfully', 'success');
        setShowPopup(false);
        navigate(`/home/contacts/${selectedData.metaData.id}`, { state: { from: '/merge', scope: 'contact' } });
        } catch (err) {
        console.error('Merge failed:', err);
        showToast('Error', 'Failed to merge contacts', 'error');
        }
    }
    
    const handleSubmit = () => {
        if(validationSchema){
            setShowPopup(true);
            
        } else {
            showToast('Error', 'Please select all fields', 'error');
        }
    };
    const onCancel = () => {
        setShowPopup(false);
    }
    const onSuccess = () => {
        ConfirmMerge();
    }

    if (isLoading) return <MergeDuplicateSkeleton />;

    if (error) return(
      <div style={{ position: 'relative' }}>
      <NoData
          ref={noDataRef}
          title={"No Contacts Found"}
          subtitle={"Something went wrong no data found against the provided contact ID's"}
        />
        </div>
    );

    // Helper: Get field value (matches logic in MergeSingleResult)
    const getFieldValue = (item: any, key: string): string => {
        const value = item?.[key];
        if (typeof value === 'object' && value !== null) return '-';
        return value !== undefined && value !== null ? value : '-';
    };

    // Helper: Get organization names
    const getOrganizationNames = (organizations: any[] = []): string => {
        if (!organizations || organizations.length === 0) return '-';
        const uniqueNames = Array.from(
            new Set(
                organizations
                    .map((item) => item?.organization?.name)
                    .filter(Boolean) as string[]
            )
        );
        return uniqueNames.join(", ");
    };

    // Helper: Get organization IDs
    const getOrganizationsIDS = (organizations: any[]): number[] => {
        if (!organizations) return [];
        const uniqueIds = new Set(
            organizations
                .map(item => item.organization?.id)
                .filter((id): id is number => id !== undefined)
        );
        return Array.from(uniqueIds);
    };

    // Helper: Check if value should be skipped
    const shouldSkipValue = (value: any): boolean => {
        return value === null || value === undefined || value === '' || value === '-';
    };

    // NEW: Select ALL fields from a contact AND sync with Formik (skip empty values)
    const handleSelectAllFieldsFromContact = (contact: any, formik: any) => {
        // 1. Update primary ID and image (always update these)
        handleMetaIdUpdate(contact.id, contact.image);
        formik.setFieldValue('dataID', contact.id);
        
        // 2. Update standard meta fields (skip if empty)
        ['firstName', 'lastName', 'fullName', 'email'].forEach(key => {
            const value = getFieldValue(contact, key);
            
            // ✅ SKIP if empty or '-'
            if (shouldSkipValue(value)) return;
            
            // Update your state
            updateMetaDataField(key, {
                data_id: contact.id,
                value: value,
            });
            
            // ✅ ALSO update Formik
            formik.setFieldValue(key, value);
        });
        
        // 3. Handle contactOrganizations specially (skip if empty)
        if (contact.contactOrganizations) {
            const orgNames = getOrganizationNames(contact.contactOrganizations);
            
            // ✅ SKIP if no organizations
            if (!shouldSkipValue(orgNames) && orgNames !== '-') {
                const orgIds = getOrganizationsIDS(contact.contactOrganizations);
                
                updateMetaDataField('contactOrganizations', {
                    data_id: contact.id,
                    orgIds: orgIds,
                    orgNames: orgNames,
                });
                
                // ✅ Sync Formik
                formik.setFieldValue('contactOrganizations', orgNames);
            }
        }
        
        // 4. Update dynamic submission fields (skip if empty)
        dynamicFields?.submissionFields?.forEach(field => {
            const matchedField = contact?.cfr?.formSubmissions?.submissionValues?.find(
                (submission: any) => submission.field?.id === field.id
            );
            const value = matchedField?.value ?? '-';
            
            // ✅ SKIP if empty or '-'
            if (shouldSkipValue(value)) return;
            
            const fieldKey = `${field.name}_${field.id}`;
            
            // Update your state
            updateFields(contact.id, field.id, field.name, value);
            
            // ✅ ALSO update Formik
            formik.setFieldValue(fieldKey, value);
        });
        
        // 5. Update dynamic CRM fields (skip if empty)
        dynamicFields?.crmFieldValues?.forEach(field => {
            const matchedField = contact?.cfr?.crmFieldValues?.find(
                (submission: any) => submission.field?.id === field.id
            );
            const value = matchedField?.value ?? '-';
            
            // ✅ SKIP if empty or '-'
            if (shouldSkipValue(value)) return;
            
            const fieldKey = `${field.name}_${field.id}`;
            
            // Update your state
            updateFields(contact.id, field.id, field.name, value);
            
            // ✅ ALSO update Formik
            formik.setFieldValue(fieldKey, value);
        });

        // 🆕 6. AUTO-FILL: For empty standard meta fields, look for data from other contacts
        ['firstName', 'lastName', 'fullName', 'email'].forEach(key => {
            const primaryValue = getFieldValue(contact, key);
            
            // If primary contact field is empty, look for data in other contacts
            if (shouldSkipValue(primaryValue)) {
                const otherContacts = recordData?.filter((c: any) => c.id !== contact.id) || [];
                
                for (const otherContact of otherContacts) {
                    const otherValue = getFieldValue(otherContact, key);
                    
                    if (!shouldSkipValue(otherValue)) {
                        // Found data in another contact, auto-select it
                        updateMetaDataField(key, {
                            data_id: otherContact.id,
                            value: otherValue,
                        });
                        
                        // Sync Formik
                        formik.setFieldValue(key, otherValue);
                        break; // Take the first non-empty value found
                    }
                }
            }
        });

        // 🆕 7. AUTO-FILL: For empty contactOrganizations, look for data from other contacts
        const primaryOrgNames = getOrganizationNames(contact.contactOrganizations);
        if (shouldSkipValue(primaryOrgNames) || primaryOrgNames === '-') {
            const otherContacts = recordData?.filter((c: any) => c.id !== contact.id) || [];
            
            for (const otherContact of otherContacts) {
                const otherOrgNames = getOrganizationNames(otherContact.contactOrganizations);
                
                if (!shouldSkipValue(otherOrgNames) && otherOrgNames !== '-') {
                    // Found organizations in another contact, auto-select them
                    const orgIds = getOrganizationsIDS(otherContact.contactOrganizations);
                    
                    updateMetaDataField('contactOrganizations', {
                        data_id: otherContact.id,
                        orgIds: orgIds,
                        orgNames: otherOrgNames,
                    });
                    
                    // Sync Formik
                    formik.setFieldValue('contactOrganizations', otherOrgNames);
                    break; // Take the first non-empty value found
                }
            }
        }

        // 🆕 8. AUTO-FILL: For empty dynamic submission fields, look for data from other contacts
        dynamicFields?.submissionFields?.forEach(field => {
            const matchedField = contact?.cfr?.formSubmissions?.submissionValues?.find(
                (submission: any) => submission.field?.id === field.id
            );
            const primaryValue = matchedField?.value ?? '-';
            
            // If primary contact field is empty, look for data in other contacts
            if (shouldSkipValue(primaryValue)) {
                const otherContacts = recordData?.filter((c: any) => c.id !== contact.id) || [];
                
                for (const otherContact of otherContacts) {
                    const otherMatchedField = otherContact?.cfr?.formSubmissions?.submissionValues?.find(
                        (submission: any) => submission.field?.id === field.id
                    );
                    const otherValue = otherMatchedField?.value ?? '-';
                    
                    if (!shouldSkipValue(otherValue)) {
                        // Found data in another contact, auto-select it
                        updateFields(otherContact.id, field.id, field.name, otherValue);
                        
                        const fieldKey = `${field.name}_${field.id}`;
                        formik.setFieldValue(fieldKey, otherValue);
                        break; // Take the first non-empty value found
                    }
                }
            }
        });

        // 🆕 9. AUTO-FILL: For empty dynamic CRM fields, look for data from other contacts
        dynamicFields?.crmFieldValues?.forEach(field => {
            const matchedField = contact?.cfr?.crmFieldValues?.find(
                (submission: any) => submission.field?.id === field.id
            );
            const primaryValue = matchedField?.value ?? '-';
            
            // If primary contact field is empty, look for data in other contacts
            if (shouldSkipValue(primaryValue)) {
                const otherContacts = recordData?.filter((c: any) => c.id !== contact.id) || [];
                
                for (const otherContact of otherContacts) {
                    const otherMatchedField = otherContact?.cfr?.crmFieldValues?.find(
                        (submission: any) => submission.field?.id === field.id
                    );
                    const otherValue = otherMatchedField?.value ?? '-';
                    
                    if (!shouldSkipValue(otherValue)) {
                        // Found data in another contact, auto-select it
                        updateFields(otherContact.id, field.id, field.name, otherValue);
                        
                        const fieldKey = `${field.name}_${field.id}`;
                        formik.setFieldValue(fieldKey, otherValue);
                        break; // Take the first non-empty value found
                    }
                }
            }
        });
        
        // 🎯 Force Formik to re-validate after bulk update
        setTimeout(() => {
            formik.validateForm();
        }, 0);
    };
        
  return (
    <>
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            innerRef={formikRef}
        >
            {(formik) => {
                const { errors, submitCount } = formik;

                // nothing to run here; defaults handled by component-level effect

                return (
            <Form>
                <Container theme={theme} className='rounded-0'>
                <div className="d-flex justify-content-between mt-0">
                    <div className="d-flex align-items-center gap-2">
                        
                        <TitleGroup className='mb-1'>
                            <TitleWrapper className='align-items-center'>
                                <ArrowIcon className="cursor-pointer" onClick={() => navigate(-1)} color={theme.CorporateTheme.colors.textSecondary} />
                                <Title theme={theme}>{'Merge Duplicate Contacts'}</Title>
                                <TitleCount theme={theme}>({recordData?.length})</TitleCount>
                            </TitleWrapper>
                            <StyledText fontSize="14px" fontWeight={300} textColor={theme.CorporateTheme.colors.textSecondary}>
                                {'All duplicate contacts will show here' }
                            </StyledText>
                        </TitleGroup>
                        
                    </div>
                    <Button theme={theme} type="submit" $variant="add">
                        <TwoUsersIcon color={theme.CorporateTheme.colors.background} />
                        <label className='cursor-pointer'>
                            Merge Duplicates
                        </label>
                    </Button>
                </div>
                </Container>
        
                <div className="container-fluid" style={{ backgroundColor: theme.CorporateTheme.colors.bannerBg, height: 'calc(100vh - 205px)' }}>
                        <div className="row pt-4 px-2">
                        <div className="col-9">
                            <div className='d-flex gap-4 justify-content-between'>
                                <StyledText fontSize='16px'>Duplicate Results</StyledText>
                                {submitCount > 0 && Object.keys(errors).length > 0 && 
                                    <StyledText
                                        textColor='red'
                                    >
                                        Please select all fields.
                                    </StyledText>
                                }
                                
                            </div>
                            <WhiteContainer theme={theme} style={{ height: 'calc(100vh - 290px)', overflow: 'auto', border: `${(submitCount > 0 && Object.keys(errors).length > 0) ? '1px solid red' : ''}` }}>
                                
                                <MergeSingleResult 
                                    scope='CONTACTS'
                                    data={recordData} 
                                    columns={allFields} 
                                    dynamicFields={dynamicFields} 
                                    initialState={selectedData}
                                    handelChange={formik.handleChange}
                                    onClick={(field_key, value) => formik.setFieldValue(field_key, value)}
                                    handlePrimaryID={handleMetaIdUpdate}
                                    handleMeta={updateMetaDataField}
                                    handleFormFields={updateFields}
                                    onSelectAllFields={(contact) => handleSelectAllFieldsFromContact(contact, formik)}
                                />
                                <PrivateNotesMerge
                                    scope='CONTACTS'
                                    data={recordData}
                                    initialState={selectedData}
                                    updataNotes={updatePrivateNotes}
                                />
                            </WhiteContainer>
                        </div>
                        <div className="col-3">
                            <StyledText fontSize='16px'>Merge Preview</StyledText>
                                    <MergePreview scope='CONTACT' data={selectedData} columns={allFields} dynamicFields={dynamicFields} />
                        </div>
                        </div>
                </div>
            </Form>
            );
            }}
        </Formik>
        {showPopup &&
            <MergeConfirmPopup 
                title='Confirm Merge?'
                subTitle='Are you sure you want to merge these contacts?  Merging contacts is an irreversible process. Once merged, cannot be undo'
                onCancel={() => onCancel()}
                onConfirm={() => onSuccess()}
                loading={loadingMerge}
            />
        }
    </>
  )
}

const WhiteContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.colors.contentBackground,
  borderRadius: '5px',
  padding: '0px 0px 12px 2px',
  marginTop: '20px',
}));

export default MergeContacts